import { CSSProperties, useLayoutEffect, useRef, useState } from "react";

const ASSET_BASE = `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/tactics-banner`;

type FigureId = "cameron" | "bachoore" | "tekkz" | "boras" | "zac";
type Format = "desktop" | "tablet" | "mobile";

const PAGE_FILL = "#14191f";
const HAIRLINE = "rgba(195,209,205,0.55)";
const SHADOW = "drop-shadow(0 18px 34px rgba(0,0,0,0.65))";

const FIGURES: Record<
  FigureId,
  {
    src: string;
    name: string;
    club: string;
    achievement: string;
    filter: string;
  }
> = {
  cameron: {
    src: "cameron.png",
    name: "Cameron Rock",
    club: "Aston Villa Esports",
    achievement: "Top 20 FC Pro Global",
    filter:
      "saturate(0.8) contrast(1.01) brightness(1) hue-rotate(-3deg) blur(0.35px)",
  },
  bachoore: {
    src: "bachoore.png",
    name: "Manuel Bachoore",
    club: "Team Liquid (Gullit)",
    achievement: "World Champion '23 & '25",
    filter: "saturate(0.8) contrast(1.05) brightness(0.99) hue-rotate(-3deg)",
  },
  tekkz: {
    src: "tekkz.png",
    name: "Tekkz",
    club: "RB Leipzig Esports",
    achievement: "eCL & ePL Champion",
    filter: "saturate(0.76) contrast(1.04) brightness(1.06) hue-rotate(-4deg)",
  },
  boras: {
    src: "boras.png",
    name: "BorasLegend",
    club: "FC All-Time Greatest",
    achievement: "Creator & Former Pro",
    filter: "saturate(0.82) contrast(1.06) brightness(0.98) hue-rotate(-3deg)",
  },
  zac: {
    src: "zac.png",
    name: "Zac Moore",
    club: "Aston Villa Esports",
    achievement: "5x Worlds Champs Coach",
    filter:
      "saturate(0.8) contrast(1) brightness(1.1) hue-rotate(-3deg) blur(0.35px)",
  },
};

// Positions are % of the stage, so the stage can be cropped but never scaled.
type Placement = [
  left: number,
  top: number,
  width: number,
  height: number,
  z: number,
];

interface Caption {
  x: number;
  bottom: number;
  name: string;
  nameWeight: number;
  club?: string;
  achievement?: string;
  gap: string;
  hairline: string;
}

interface StageLayout {
  width: number;
  height: number;
  nameTracking: string;
  clubTracking: string;
  figures: Partial<Record<FigureId, Placement>>;
  captions: Partial<Record<FigureId, Caption>>;
}

const FIVE_UP: StageLayout["figures"] = {
  cameron: [13.09, 12.55, 21.02, 157.7, 4],
  bachoore: [30.38, 4.38, 15.13, 168.4, 5],
  tekkz: [39.14, 4.44, 19.66, 131.6, 7],
  boras: [55.46, 7.84, 13.96, 108.6, 5],
  zac: [65.16, 14.36, 18.79, 124.1, 4],
};

// Desktop drops every figure 9% to clear the page heading over the banner.
const FIVE_UP_DESKTOP = Object.fromEntries(
  Object.entries(FIVE_UP).map(([id, [left, top, ...rest]]) => [
    id,
    [left, top + 9, ...rest],
  ]),
) as StageLayout["figures"];

const LAYOUTS: Record<Format, StageLayout> = {
  desktop: {
    width: 1225,
    height: 228,
    nameTracking: "0.04em",
    clubTracking: "0.16em",
    figures: FIVE_UP_DESKTOP,
    captions: {
      cameron: {
        x: 25.59,
        bottom: 2.6,
        name: "1cqw",
        nameWeight: 600,
        club: ".62cqw",
        achievement: ".66cqw",
        gap: ".42cqw",
        hairline: "1cqw",
      },
      bachoore: {
        x: 37.79,
        bottom: 5.25,
        name: "1.15cqw",
        nameWeight: 600,
        club: ".68cqw",
        achievement: ".72cqw",
        gap: ".42cqw",
        hairline: "1cqw",
      },
      tekkz: {
        x: 50,
        bottom: 7.87,
        name: "1.6cqw",
        nameWeight: 700,
        club: ".82cqw",
        achievement: ".9cqw",
        gap: ".42cqw",
        hairline: "1cqw",
      },
      boras: {
        x: 62.21,
        bottom: 5.25,
        name: "1.15cqw",
        nameWeight: 600,
        club: ".68cqw",
        achievement: ".72cqw",
        gap: ".42cqw",
        hairline: "1cqw",
      },
      zac: {
        x: 74.41,
        bottom: 2.6,
        name: "1cqw",
        nameWeight: 600,
        club: ".62cqw",
        achievement: ".66cqw",
        gap: ".42cqw",
        hairline: "1cqw",
      },
    },
  },
  tablet: {
    width: 817,
    height: 152,
    nameTracking: "0.02em",
    clubTracking: "0.03em",
    figures: FIVE_UP,
    captions: {
      cameron: {
        x: 25.59,
        bottom: 3.5,
        name: "10px",
        nameWeight: 600,
        achievement: "7px",
        gap: "4px",
        hairline: "4px",
      },
      bachoore: {
        x: 37.79,
        bottom: 6.5,
        name: "12px",
        nameWeight: 600,
        achievement: "7.5px",
        gap: "4px",
        hairline: "4px",
      },
      tekkz: {
        x: 50,
        bottom: 9.5,
        name: "17px",
        nameWeight: 700,
        achievement: "9px",
        gap: "4px",
        hairline: "5px",
      },
      boras: {
        x: 62.21,
        bottom: 6.5,
        name: "12px",
        nameWeight: 600,
        achievement: "7.5px",
        gap: "4px",
        hairline: "4px",
      },
      zac: {
        x: 74.41,
        bottom: 3.5,
        name: "10px",
        nameWeight: 600,
        achievement: "7px",
        gap: "4px",
        hairline: "4px",
      },
    },
  },
  mobile: {
    width: 560,
    height: 152,
    nameTracking: "0.03em",
    clubTracking: "0.04em",
    figures: {
      bachoore: [18.18, 4.38, 22.08, 168.4, 5],
      tekkz: [34.22, 4.44, 28.68, 131.6, 7],
      boras: [61.14, 7.84, 20.37, 108.6, 5],
    },
    captions: {
      bachoore: {
        x: 29,
        bottom: 5,
        name: "13px",
        nameWeight: 600,
        club: "9px",
        achievement: "8.5px",
        gap: "4px",
        hairline: "5px",
      },
      tekkz: {
        x: 50,
        bottom: 8,
        name: "19px",
        nameWeight: 700,
        club: "10.5px",
        achievement: "10px",
        gap: "5px",
        hairline: "6px",
      },
      boras: {
        x: 71,
        bottom: 5,
        name: "13px",
        nameWeight: 600,
        club: "9px",
        achievement: "8.5px",
        gap: "4px",
        hairline: "5px",
      },
    },
  },
};

const formatForWidth = (width: number): Format =>
  width >= 1024 ? "desktop" : width >= 640 ? "tablet" : "mobile";

const TEKKZ_SHIRT_MASK: CSSProperties = {
  maskImage: `url(${ASSET_BASE}/tekkz.png)`,
  maskSize: "100% 100%",
  WebkitMaskImage: `url(${ASSET_BASE}/tekkz.png)`,
  WebkitMaskSize: "100% 100%",
  background:
    "linear-gradient(to top, rgba(52,80,76,0.88) 0%, rgba(62,92,88,0.72) 26%, rgba(78,110,105,0.46) 40%, rgba(130,158,152,0.08) 50%, rgba(0,0,0,0) 56%)",
  mixBlendMode: "multiply",
};

const Hairline = ({ width, toLeft }: { width: string; toLeft?: boolean }) => (
  <span
    className="block h-px"
    style={{
      width,
      background: `linear-gradient(to ${toLeft ? "left" : "right"}, rgba(195,209,205,0), ${HAIRLINE})`,
    }}
  />
);

// Grading overlays overhang the stage so the tint doesn't stop at its edges.
const OVERHANG = 2000;

const Stage = ({ layout }: { layout: StageLayout }) => (
  <div
    className="absolute top-0 left-1/2 -translate-x-1/2 @container"
    style={{ width: layout.width, height: layout.height }}
  >
    {(Object.entries(layout.figures) as [FigureId, Placement][]).map(
      ([id, [left, top, width, height, z]]) => (
        <div
          key={id}
          className="absolute isolate"
          style={{
            left: `${left}%`,
            top: `${top}%`,
            width: `${width}%`,
            height: `${height}%`,
            zIndex: z,
          }}
        >
          <img
            src={`${ASSET_BASE}/${FIGURES[id].src}`}
            alt={FIGURES[id].name}
            className="block size-full"
            style={{ filter: `${FIGURES[id].filter} ${SHADOW}` }}
          />
          {id === "tekkz" && (
            <div className="absolute inset-0" style={TEKKZ_SHIRT_MASK} />
          )}
        </div>
      ),
    )}
    <div
      className="absolute inset-y-0 z-7 pointer-events-none mix-blend-soft-light"
      style={{
        left: -OVERHANG,
        right: -OVERHANG,
        background: `radial-gradient(${layout.width * 1.2}px ${layout.height * 1.5}px at 50% 12%, rgba(130,255,220,0.16) 0%, rgba(6,34,31,0.30) 62%, rgba(2,16,15,0.55) 100%)`,
      }}
    />
    <div
      className="absolute inset-y-0 z-7 pointer-events-none mix-blend-multiply"
      style={{
        left: -OVERHANG,
        right: -OVERHANG,
        background:
          "linear-gradient(to top, rgba(4,26,24,0.55) 0%, rgba(4,26,24,0.12) 38%, rgba(4,26,24,0) 62%)",
      }}
    />
    <div
      className="absolute bottom-0 h-[48%] z-8 pointer-events-none"
      style={{
        left: -OVERHANG,
        right: -OVERHANG,
        background:
          "linear-gradient(to bottom, rgba(4,16,15,0) 0%, rgba(4,16,15,0.55) 22%, rgba(4,16,15,0.88) 44%, #04100f 72%)",
      }}
    />
    {(Object.entries(layout.captions) as [FigureId, Caption][]).map(
      ([id, caption]) => (
        <div
          key={id}
          className="absolute z-9 -translate-x-1/2 text-center whitespace-nowrap leading-none"
          style={{ left: `${caption.x}%`, bottom: `${caption.bottom}%` }}
        >
          <div
            className="font-oswald uppercase text-[#2fe0a0]"
            style={{
              fontSize: caption.name,
              fontWeight: caption.nameWeight,
              letterSpacing: layout.nameTracking,
            }}
          >
            {FIGURES[id].name}
          </div>
          {caption.club && (
            <div
              className="flex items-center justify-center"
              style={{ gap: caption.gap, marginTop: caption.gap }}
            >
              <Hairline width={caption.hairline} />
              <span
                className="font-oswald font-medium uppercase text-[#e9f3f0]"
                style={{
                  fontSize: caption.club,
                  letterSpacing: layout.clubTracking,
                }}
              >
                {FIGURES[id].club}
              </span>
              <Hairline width={caption.hairline} toLeft />
            </div>
          )}
          {caption.achievement && (
            <div
              className="font-medium text-[#c3d1cd]"
              style={{ fontSize: caption.achievement, marginTop: caption.gap }}
            >
              {FIGURES[id].achievement}
            </div>
          )}
        </div>
      ),
    )}
  </div>
);

// Fixed-size stages centred in the banner: narrower screens crop the sides,
// wider ones show more backdrop. Nothing is ever scaled to fit.
const TacticsBanner = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(() => window.innerWidth);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const format = formatForWidth(width);
  const layout = LAYOUTS[format];
  const edgeFadeWidth = Math.max(64, (width - layout.width) / 2 + 90);
  const edgeFade = (side: "left" | "right"): CSSProperties => ({
    width: edgeFadeWidth,
    [side]: 0,
    background: `linear-gradient(to ${side === "left" ? "right" : "left"}, ${PAGE_FILL} 0%, rgba(20,25,31,0.85) 30%, rgba(20,25,31,0.4) 65%, rgba(20,25,31,0) 100%)`,
  });

  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ height: layout.height, background: PAGE_FILL }}
    >
      <img
        src={`${ASSET_BASE}/backdrop.svg`}
        alt=""
        className="absolute inset-0 size-full object-cover object-center"
      />
      <Stage layout={layout} />
      {format === "desktop" && (
        <>
          <div
            className="absolute inset-y-0 z-10 pointer-events-none"
            style={edgeFade("left")}
          />
          <div
            className="absolute inset-y-0 z-10 pointer-events-none"
            style={edgeFade("right")}
          />
        </>
      )}
    </div>
  );
};

export default TacticsBanner;
